import Quill, { Delta } from 'quill/core';
import Clipboard from 'quill/modules/clipboard';

let registeredClipboard: typeof Clipboard | null = null;

const ALLOWED_FORMATS = new Set([
    'bold',
    'italic',
    'underline',
    'link',
    'list',
    'indent',
]);

const DIVIDER_MARKER = '---';

// taVar chips (trigger-automation message variables) survive cut/paste as-is:
// the embed IS the persisted wire format and the blot re-validates the payload
// on create.
const ALLOWED_EMBEDS = new Set(['taVar']);

/**
 * Per-editor widening of the two allow-lists, read from `modules.clipboard`.
 * An editor may only widen them for formats its own serializer keeps on save —
 * otherwise the paste renders content that silently dies at the next save.
 */
export type PlainClipboardOptions = {
    extraFormats?: string[];
    extraEmbeds?: string[];
};

function isEmbedValue(
    v: unknown,
): v is { denotationChar?: string; value?: string; label?: string } {
    return typeof v === 'object' && v !== null;
}

/**
 * Display text of an embed that has one, `null` for embeds that are pure
 * content (e.g. inlineImage) and are dropped instead.
 *
 * Read structurally rather than by blot name: a mention (user, group, hashtag,
 * table, attachment) carries `denotationChar` + `value`, while a chip such as an
 * AI source reference carries a `label`. Its id only means something inside the
 * conversation that produced it, so what travels is the text, not the chip.
 *
 * An inline image matches the `label` branch too, through its caption. Leaving
 * the caption behind is preferable to dropping the image without a trace.
 */
function embedToText(insert: Record<string, unknown>): string | null {
    const mention = insert['mention'];
    if (isEmbedValue(mention)) {
        return (mention.denotationChar ?? '') + (mention.value ?? '') || null;
    }
    const value = Object.values(insert)[0];
    if (isEmbedValue(value) && typeof value.label === 'string') {
        return value.label || null;
    }
    return null;
}

// Mirrors the h1-h6 CSS rules in quill-base.css: h1 collapses to h2 (both typo-t4), h5/h6 are normal text so dropped.
const HEADER_REMAP: Record<number, number | null> = {
    1: 2,
    2: 2,
    3: 3,
    4: 4,
    5: null,
    6: null,
};

function sanitizeValue(key: string, value: unknown): unknown {
    if (key === 'header' && typeof value === 'number') {
        return HEADER_REMAP[value] ?? null;
    }
    return value;
}

// Strips unsupported formats from a pasted Delta: embeds become plain text (or are dropped), attributes are whitelisted and sanitized.
export function filterDelta(
    delta: Delta,
    options: PlainClipboardOptions = {},
): Delta {
    const allowedFormats = options.extraFormats?.length
        ? new Set([...ALLOWED_FORMATS, ...options.extraFormats])
        : ALLOWED_FORMATS;
    const allowedEmbeds = options.extraEmbeds?.length
        ? new Set([...ALLOWED_EMBEDS, ...options.extraEmbeds])
        : ALLOWED_EMBEDS;

    const ops = (delta.ops ?? [])
        .map((op) => {
            if (typeof op.insert === 'object' && op.insert !== null) {
                const insert = op.insert;
                if ([...allowedEmbeds].some((name) => name in insert)) {
                    return op;
                }
                const text = embedToText(insert);
                return text ? { insert: text } : null;
            }
            if (!op.attributes) return op;
            // A divider travels as the literal "---" carrying the attribute:
            // stripping the format alone would paste the marker as visible text.
            if (op.attributes['divider'] && !allowedFormats.has('divider')) {
                return null;
            }
            const filtered: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(op.attributes)) {
                if (!allowedFormats.has(key)) continue;
                const sanitized = sanitizeValue(key, value);
                if (sanitized !== null) {
                    filtered[key] = sanitized;
                }
            }
            return Object.keys(filtered).length > 0
                ? { ...op, attributes: filtered }
                : { insert: op.insert };
        })
        .filter((op): op is NonNullable<typeof op> => op !== null);
    return new Delta(ops);
}

/**
 * Subclasses whatever is registered as `modules/clipboard` at call time rather
 * than Quill's own class, so a clipboard another module installed keeps working
 * underneath: quill-table-up's rebuilds a pasted table's structure, which the
 * generic blot matchers cannot do from the copied HTML alone.
 */
function buildPlainClipboard(base: typeof Clipboard): typeof Clipboard {
    return class PlainClipboard extends base {
        constructor(...args: ConstructorParameters<typeof Clipboard>) {
            super(...args);
            // A divider is copied out as a bare <hr>: the marker text lives
            // inside a void element and never reaches the clipboard HTML.
            this.addMatcher('HR', () =>
                new Delta().insert(DIVIDER_MARKER, { divider: true }),
            );
        }

        override onCaptureCopy(e: ClipboardEvent, isCut = false): void {
            const [range] = this.quill.selection.getRange();
            if (range == null && !isCut) return;
            super.onCaptureCopy(e, isCut);
        }

        override onCapturePaste(e: ClipboardEvent) {
            e.preventDefault();
            const clipboardData: DataTransfer | null = e.clipboardData;
            if (!clipboardData || clipboardData.types.includes('Files')) return;

            const range = this.quill.getSelection();
            if (!range) return;

            const html = clipboardData.getData('text/html');
            let delta: Delta;

            if (html) {
                delta = filterDelta(
                    this.convert({ html, text: '' }),
                    this.options as PlainClipboardOptions,
                );
            } else {
                const text = clipboardData.getData('text/plain');
                delta = new Delta().insert(text);
            }

            const index = range.index + delta.length();
            this.quill.updateContents(
                new Delta()
                    .retain(range.index)
                    .delete(range.length)
                    .concat(delta),
                'user',
            );
            this.quill.setSelection(index, 0, 'silent');
            this.quill.scrollSelectionIntoView();
        }
    };
}

export function registerQuillPlainClipboard(): void {
    const base = Quill.import('modules/clipboard') as typeof Clipboard;
    // Already ours, and on the same base: re-registering would stack a second
    // subclass on every editor construction.
    if (base === registeredClipboard) return;

    registeredClipboard = buildPlainClipboard(base);
    Quill.register('modules/clipboard', registeredClipboard, true);
}
